// ai.js — AI assistant client for Peerly
// Supports any OpenAI-compatible API (OpenRouter, OpenAI, local LLMs, etc.)
// User provides their own endpoint, API key, and model via settings.

import { AI } from './config.js';

/**
 * Load AI settings from localStorage, falling back to defaults.
 * @returns {{ endpoint: string, apiKey: string, model: string }}
 */
export function getSettings() {
  return {
    endpoint:
      localStorage.getItem(AI.STORAGE_KEYS.ENDPOINT) || AI.DEFAULT_ENDPOINT,
    apiKey: localStorage.getItem(AI.STORAGE_KEYS.API_KEY) || '',
    model:
      localStorage.getItem(AI.STORAGE_KEYS.MODEL) || AI.DEFAULT_MODEL,
  };
}

/**
 * Save AI settings to localStorage.
 * @param {{ endpoint?: string, apiKey?: string, model?: string }} settings
 */
export function saveSettings(settings) {
  if (settings.endpoint !== undefined) {
    localStorage.setItem(AI.STORAGE_KEYS.ENDPOINT, settings.endpoint);
  }
  if (settings.apiKey !== undefined) {
    localStorage.setItem(AI.STORAGE_KEYS.API_KEY, settings.apiKey);
  }
  if (settings.model !== undefined) {
    localStorage.setItem(AI.STORAGE_KEYS.MODEL, settings.model);
  }
}

/**
 * Check if AI is configured (has an API key).
 * @returns {boolean}
 */
export function isConfigured() {
  return !!localStorage.getItem(AI.STORAGE_KEYS.API_KEY);
}

/**
 * Clear all AI settings from localStorage.
 */
export function clearSettings() {
  localStorage.removeItem(AI.STORAGE_KEYS.ENDPOINT);
  localStorage.removeItem(AI.STORAGE_KEYS.API_KEY);
  localStorage.removeItem(AI.STORAGE_KEYS.MODEL);
}

/**
 * Send a query to the AI API with streaming response.
 * Calls onChunk(text) for each streamed token delta.
 * Calls onComplete(fullText) when done.
 * Calls onError(message) on failure.
 *
 * @param {string} query — user's question
 * @param {(text: string) => void} onChunk — called with each text delta
 * @param {(text: string) => void} onComplete — called with full response
 * @param {(message: string) => void} onError — called on error
 * @returns {AbortController} controller to abort the request
 */
export function ask(query, onChunk, onComplete, onError) {
  const settings = getSettings();

  if (!settings.apiKey) {
    onError('AI not configured. Use /ai to set your API key.');
    return null;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI.TIMEOUT_MS);

  const body = {
    model: settings.model,
    messages: [
      { role: 'system', content: AI.SYSTEM_PROMPT },
      { role: 'user', content: query },
    ],
    max_tokens: AI.MAX_TOKENS,
    temperature: AI.TEMPERATURE,
    stream: true,
  };

  fetch(settings.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.apiKey}`,
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  })
    .then(async (res) => {
      clearTimeout(timeoutId);

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        let msg = `API error ${res.status}`;
        try {
          const errJson = JSON.parse(errText);
          msg = errJson.error?.message || errJson.error || msg;
        } catch {
          if (errText) msg = `${msg}: ${errText.slice(0, 200)}`;
        }
        onError(msg);
        return;
      }

      if (!res.body) {
        const text = await res.text().catch(() => '');
        try {
          const json = JSON.parse(text);
          const content = json.choices?.[0]?.message?.content || '';
          if (content) {
            onChunk(content);
            onComplete(content);
          } else {
            onError('Empty response from API');
          }
        } catch {
          onError('Failed to parse non-streaming response');
        }
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data: ')) continue;

            const data = trimmed.slice(6);
            if (data === '[DONE]') {
              onComplete(fullText);
              return;
            }

            try {
              const json = JSON.parse(data);
              const delta = json.choices?.[0]?.delta?.content;
              if (delta) {
                fullText += delta;
                onChunk(delta);
              }
            } catch {
              // Skip malformed SSE lines
            }
          }
        }
        onComplete(fullText);
      } catch (err) {
        if (err.name === 'AbortError') {
          onError('Request timed out or was cancelled.');
        } else {
          onError(err.message || 'Streaming error');
        }
      }
    })
    .catch((err) => {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        onError('Request timed out or was cancelled.');
      } else {
        onError(err.message || 'Network error');
      }
    });

  return controller;
}
